'use client';

import { motion } from 'motion/react';
import { Sparkles, TrendingUp, Bug, Shield } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import LandingNavbar from './LandingNavbar';
import FooterSection from './FooterSection';
import { LocaleProvider } from '@/features/marketing/hooks/useLocale';
import changelogData from '@/data/changelog.json';

interface ChangelogEntry {
  version: string;
  date: string;
  changes: {
    features: string[];
    improvements: string[];
    fixes: string[];
    security: string[];
  };
}

const CATEGORIES = [
  {
    key: 'features' as const,
    label: 'New Features',
    icon: Sparkles,
    color: 'text-amber-400',
    dotColor: 'bg-amber-400',
  },
  {
    key: 'improvements' as const,
    label: 'Improvements',
    icon: TrendingUp,
    color: 'text-blue-400',
    dotColor: 'bg-blue-400',
  },
  {
    key: 'fixes' as const,
    label: 'Bug Fixes',
    icon: Bug,
    color: 'text-emerald-400',
    dotColor: 'bg-emerald-400',
  },
  {
    key: 'security' as const,
    label: 'Security',
    icon: Shield,
    color: 'text-purple-400',
    dotColor: 'bg-purple-400',
  },
] as const;

export default function ChangelogPage() {
  const entries = changelogData.entries as ChangelogEntry[];

  return (
    <LocaleProvider>
      <div className="min-h-screen bg-[#050505] text-white">
        <LandingNavbar surface="dark" />

        {/* Hero */}
        <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-sm font-medium mb-6">
                <Sparkles className="h-4 w-4" />
                Product Updates
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold mt-4 bg-gradient-to-b from-white to-white/60 bg-clip-text text-transparent">
                Changelog
              </h1>
              <p className="text-lg text-white/50 mt-4 max-w-2xl mx-auto">
                New features, improvements, and fixes shipped to CMG Workflow CRM.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Timeline */}
        <section className="pb-24 px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-[23px] top-0 bottom-0 w-px bg-white/10 hidden sm:block" />

              <div className="space-y-12">
                {entries.map((entry, index) => (
                  <motion.article
                    key={entry.version}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="relative"
                  >
                    {/* Timeline dot */}
                    <div className="absolute left-[16px] top-2 w-[15px] h-[15px] rounded-full bg-gradient-to-br from-amber-400 to-amber-600 border-2 border-[#050505] hidden sm:block z-10" />

                    <div className="sm:ml-14">
                      {/* Version header */}
                      <div className="flex flex-wrap items-center gap-3 mb-4">
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-amber-500/20 to-amber-600/20 border border-amber-500/30 text-amber-400 text-sm font-semibold">
                          v{entry.version}
                        </span>
                        <time className="text-sm text-white/40">
                          {format(parseISO(entry.date), 'MMMM d, yyyy')}
                        </time>
                      </div>

                      {/* Changes card */}
                      <div className="rounded-xl bg-white/[0.03] border border-white/[0.06] p-6 hover:border-white/10 transition-colors">
                        <div className="space-y-6">
                          {CATEGORIES.map(({ key, label, icon: Icon, color, dotColor }) => {
                            const raw = entry.changes[key];
                            const items = Array.isArray(raw) ? raw : raw ? [raw] : [];
                            if (items.length === 0) return null;

                            return (
                              <div key={key}>
                                <div className={`flex items-center gap-2 mb-3 ${color}`}>
                                  <Icon className="h-4 w-4" />
                                  <h3 className="text-sm font-semibold uppercase tracking-wider">
                                    {label}
                                  </h3>
                                </div>
                                <ul className="space-y-2">
                                  {/* eslint-disable @eslint-react/no-array-index-key -- changelog items are static strings with no stable ID; list is append-only and never reordered */}
                                  {items.map((item, i) => (
                                    <li
                                      key={i}
                                      className="flex items-start gap-3 text-white/70 text-sm leading-relaxed"
                                    >
                                      <span
                                        className={`mt-2 h-1.5 w-1.5 rounded-full ${dotColor} flex-shrink-0`}
                                      />
                                      {item}
                                    </li>
                                  ))}
                                  {/* eslint-enable @eslint-react/no-array-index-key */}
                                </ul>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </motion.article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <FooterSection />
      </div>
    </LocaleProvider>
  );
}
